import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col, Form, Button, Table, Modal, Badge, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaUserMd, FaUsers, FaDollarSign, FaCheckCircle, FaExclamationTriangle, FaHistory, FaPrint, FaFilter } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const DoctorSalaries = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [salaries, setSalaries] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ doctorId: '', amount: '', month: new Date().toISOString().slice(0, 7), notes: '', payDate: new Date().toISOString().split('T')[0], allowances: 0, deductions: 0, paymentMethod: 'Cash', status: 'paid' });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Modal form month/year states (separate from filter)
  const [modalMonth, setModalMonth] = useState(new Date().getMonth());
  const [modalYear, setModalYear] = useState(new Date().getFullYear());


  useEffect(() => {
    fetchSalaries();
    fetchDoctors();
  }, []);

  const fetchSalaries = async () => {
    try {
      const res = await axios.get('/api/doctor-salaries');
      setSalaries(res.data);
    } catch (err) {
      setError('Failed to load salaries');
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await axios.get('/api/doctors');
      setDoctors(res.data);
    } catch (err) {
      setError('Failed to load doctors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`/api/doctor-salaries/${editId}`, formData);
      } else {
        await axios.post('/api/doctor-salaries', formData);
      }
      setShowModal(false);
      setFormData({ doctorId: '', amount: '', month: new Date().toISOString().slice(0, 7), notes: '' });
      setEditId(null);
      fetchSalaries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save salary');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`/api/doctor-salaries/${id}`);
        fetchSalaries();
      } catch (err) {
        setError('Failed to delete salary');
      }
    }
  };

  const handleEdit = (salary) => {
    setEditId(salary.id);
    
    // Parse month from salary (format: YYYY-MM)
    const [year, month] = salary.month.split('-');
    const monthIndex = parseInt(month) - 1;
    
    setModalMonth(monthIndex);
    setModalYear(parseInt(year));
    
    setFormData({ 
      doctorId: salary.doctorId, 
      amount: salary.amount, 
      month: salary.month, 
      notes: salary.notes,
      payDate: salary.paymentDate || salary.payDate || new Date().toISOString().split('T')[0],
      allowances: salary.allowances || 0,
      deductions: salary.deductions || 0,
      paymentMethod: salary.paymentMethod || 'Cash',
      status: salary.status || 'paid'
    });
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR' });
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const filterMonth = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
  const filteredSalaries = salaries.filter(sal => sal.month === filterMonth);
  const totalMonthlyPayout = filteredSalaries.reduce((sum, sal) => sum + parseFloat(sal.amount || 0), 0);
  const paymentsMade = filteredSalaries.length;
  const pendingPayments = doctors.length - paymentsMade;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2><FaUserMd className="me-2" />Doctor Reports & Payroll</h2>
        <div>
          {(user?.role === 'admin' || user?.permissions?.paySalary) && (
            <Button variant="primary" className="me-2" onClick={() => { 
              setShowModal(true); 
              setEditId(null); 
              const currentMonth = new Date().getMonth();
              const currentYear = new Date().getFullYear();
              setModalMonth(currentMonth);
              setModalYear(currentYear);
              setFormData({ 
                doctorId: '', 
                amount: '', 
                month: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`, 
                notes: '', 
                payDate: new Date().toISOString().split('T')[0],
                allowances: 0,
                deductions: 0,
                paymentMethod: 'Cash',
                status: 'paid'
              }); 
            }}>
              <FaPlus className="me-2" /> Pay Salary
            </Button>
          )}
          {(user?.role === 'admin' || user?.permissions?.printSalaryReport) && (
            <Button variant="success">
              <FaPrint className="me-2" /> Print Report
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Month</Form.Label>
                <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                  {months.map((month, idx) => <option key={idx} value={idx}>{month}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Year</Form.Label>
                <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                  {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(year => <option key={year} value={year}>{year}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button variant="primary" className="w-100">
                <FaFilter className="me-2" /> Filter Report
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaUsers size={40} className="text-primary mb-2" />
              <h3 className="mb-0 fw-bold">{doctors.length}</h3>
              <p className="text-muted mb-0 small">Total Doctors</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaDollarSign size={40} className="text-success mb-2" />
              <h3 className="mb-0 fw-bold">{formatCurrency(totalMonthlyPayout)}</h3>
              <p className="text-muted mb-0 small">Monthly Payout</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaCheckCircle size={40} className="text-info mb-2" />
              <h3 className="mb-0 fw-bold">{paymentsMade}</h3>
              <p className="text-muted mb-0 small">Payments Made</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center py-3">
            <Card.Body>
              <FaExclamationTriangle size={40} className="text-warning mb-2" />
              <h3 className="mb-0 fw-bold">{pendingPayments}</h3>
              <p className="text-muted mb-0 small">Pending Payments</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-dark text-white py-3">
          <h5 className="mb-0 fw-bold">📋 Payroll Report - {months[selectedMonth]} {selectedYear}</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead className="bg-dark text-white">
              <tr>
                <th className="py-3">Doctor</th>
                <th className="py-3">Employee ID</th>
                <th className="py-3">Pay Date</th>
                <th className="py-3">Basic Salary</th>
                <th className="py-3">Allowances</th>
                <th className="py-3">Deductions</th>
                <th className="py-3">Net Salary</th>
                <th className="py-3">Payment Method</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSalaries.length > 0 ? filteredSalaries.map((sal) => {
                const netSalary = parseFloat(sal.amount || 0) + parseFloat(sal.allowances || 0) - parseFloat(sal.deductions || 0);
                return (
                  <tr key={sal.id}>
                    <td className="py-3">Dr. {sal.Doctor?.name}</td>
                    <td className="py-3">DOC-{sal.Doctor?.id}</td>
                    <td className="py-3">{sal.payDate ? new Date(sal.payDate).toLocaleDateString('en-GB') : new Date(sal.createdAt).toLocaleDateString('en-GB')}</td>
                    <td className="py-3 fw-bold">{formatCurrency(sal.amount)}</td>
                    <td className="py-3">{formatCurrency(sal.allowances || 0)}</td>
                    <td className="py-3">{formatCurrency(sal.deductions || 0)}</td>
                    <td className="py-3 fw-bold text-success">{formatCurrency(netSalary)}</td>
                    <td className="py-3"><Badge bg="info">{sal.paymentMethod || 'Cash'}</Badge></td>
                    <td className="py-3 text-center">
                      {(user?.role === 'admin' || user?.permissions?.editSalary) && (
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(sal)}><FaEdit /></Button>
                      )}
                      {(user?.role === 'admin' || user?.permissions?.deleteSalary) && (
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(sal.id)}><FaTrash /></Button>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">
                    ℹ️ No salary payments found for this month
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="bg-light p-3 d-flex justify-content-between align-items-center">
            <strong>Total Monthly Payout:</strong>
            <Badge bg="primary" className="fs-5 px-4 py-2">{formatCurrency(totalMonthlyPayout)}</Badge>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <h5 className="mb-0 fw-bold"><FaUsers className="me-2" />Doctor Salary Overview</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            {doctors.map((doctor) => {
              const doctorSalaries = salaries.filter(s => s.doctorId === doctor.id);
              const totalPaid = doctorSalaries.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
              const lastPayment = doctorSalaries.length > 0 ? doctorSalaries[0] : null;
              const isPaidThisMonth = filteredSalaries.some(s => s.doctorId === doctor.id);
              
              return (
                <Col md={6} lg={4} key={doctor.id} className="mb-3">
                  <Card className="border shadow-sm h-100">
                    <Card.Body>
                      <h6 className="fw-bold">Dr. {doctor.name}</h6>
                      <p className="text-muted small mb-2">{doctor.specialization}</p>
                      <div className="mb-2">
                        <strong>Base Salary:</strong> {formatCurrency(lastPayment?.amount || 0)}
                      </div>
                      <div className="mb-2">
                        <strong>Paid This Year:</strong> {formatCurrency(totalPaid)}
                      </div>
                      <div className="mb-3">
                        <strong>Last Payment:</strong> {lastPayment ? new Date(lastPayment.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                      </div>
                      <div className="d-flex gap-2">
                        {isPaidThisMonth ? (
                          <Badge bg="success" className="px-3 py-2">
                            <FaCheckCircle className="me-1" /> Paid
                          </Badge>
                        ) : (
                          (user?.role === 'admin' || user?.permissions?.paySalary) && (
                            <Button variant="outline-primary" size="sm" onClick={() => { 
                              setModalMonth(selectedMonth);
                              setModalYear(selectedYear);
                              setFormData({ 
                                doctorId: doctor.id, 
                                amount: '', 
                                month: filterMonth, 
                                notes: '', 
                                payDate: new Date().toISOString().split('T')[0], 
                                allowances: 0, 
                                deductions: 0, 
                                paymentMethod: 'Cash',
                                status: 'paid'
                              }); 
                              setShowModal(true); 
                            }}>
                              <FaDollarSign className="me-1" /> Pay Now
                            </Button>
                          )
                        )}
                        {(user?.role === 'admin' || user?.permissions?.viewDeposit) && (
                          <Button variant="outline-info" size="sm" onClick={() => navigate(`/doctor-payment-history/${doctor.id}`)}>
                            <FaHistory className="me-1" /> History
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>Pay Doctor Salary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Doctor</Form.Label>
                  <Form.Select value={formData.doctorId} onChange={(e) => setFormData({...formData, doctorId: e.target.value})} required>
                    <option value="">Choose Doctor</option>
                    {doctors.map(doc => <option key={doc.id} value={doc.id}>Dr. {doc.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pay Date</Form.Label>
                  <Form.Control type="date" value={formData.payDate} onChange={(e) => setFormData({...formData, payDate: e.target.value})} required />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Salary For Month</Form.Label>
                  <Form.Select 
                    value={modalMonth} 
                    onChange={(e) => { 
                      const m = parseInt(e.target.value); 
                      setModalMonth(m); 
                      setFormData({...formData, month: `${modalYear}-${String(m + 1).padStart(2, '0')}`}); 
                    }}
                  >
                    {months.map((month, idx) => <option key={idx} value={idx}>{month}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Salary For Year</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={modalYear} 
                    onChange={(e) => { 
                      const y = parseInt(e.target.value); 
                      setModalYear(y); 
                      setFormData({...formData, month: `${y}-${String(modalMonth + 1).padStart(2, '0')}`}); 
                    }} 
                    required 
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Basic Salary</Form.Label>
                  <Form.Control type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Allowances</Form.Label>
                  <Form.Control type="number" value={formData.allowances} onChange={(e) => setFormData({...formData, allowances: e.target.value})} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Deductions</Form.Label>
                  <Form.Control type="number" value={formData.deductions} onChange={(e) => setFormData({...formData, deductions: e.target.value})} />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select value={formData.paymentMethod} onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Net Salary</Form.Label>
                  <Form.Control type="text" value={formatCurrency(parseFloat(formData.amount || 0) + parseFloat(formData.allowances || 0) - parseFloat(formData.deductions || 0))} disabled className="bg-light fw-bold" />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Optional notes..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
            </Form.Group>
            
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="success" type="submit">
                <FaDollarSign className="me-2" /> Process Payment
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DoctorSalaries;
